import { gql } from "@apollo/client";

// ─── Fragments ───────────────────────────────────────────────────────────────

const LEAD_LIST_FIELDS = gql`
  fragment LeadListFields on LeadType {
    _id
    name
    email
    company
    size
    tier
    status
    qualificationScore
    source
    phone
    locale
    createdAt
    lastContactedAt
    assignedTo
    assignedToUser {
      _id
      firstName
      lastName
      email
    }
  }
`;

const LEAD_DETAIL_FIELDS = gql`
  fragment LeadDetailFields on LeadType {
    _id
    name
    email
    company
    size
    tier
    status
    qualificationScore
    source
    utmSource
    utmMedium
    utmCampaign
    referrer
    landingPage
    locale
    phone
    notes
    lastContactedAt
    nextFollowUpAt
    assignedTo
    assignedToUser {
      _id
      firstName
      lastName
      email
    }
    interactions {
      type
      content
      createdBy
      createdAt
    }
    convertedAt
    convertedToOrganizationId
    consentedAt
    dataRetentionExpiry
    isActive
    createdAt
    updatedAt
  }
`;

// ─── Queries ─────────────────────────────────────────────────────────────────

export const ADMIN_LEADS = gql`
  ${LEAD_LIST_FIELDS}
  query AdminLeads($filters: LeadFiltersInput, $pagination: LeadPaginationInput!) {
    adminLeads(filters: $filters, pagination: $pagination) {
      items {
        ...LeadListFields
      }
      total
      hasMore
    }
  }
`;

export const ADMIN_LEAD_DETAIL = gql`
  ${LEAD_DETAIL_FIELDS}
  query AdminLeadDetail($id: ID!) {
    adminLeadDetail(id: $id) {
      ...LeadDetailFields
    }
  }
`;

export const ADMIN_LEAD_STATS = gql`
  query AdminLeadStats($filters: LeadFiltersInput) {
    adminLeadStats(filters: $filters) {
      totalLeads
      byStatus {
        status
        count
      }
      byTier {
        tier
        count
      }
      conversionRate
      avgDaysToClose
      newLeadsThisWeek
    }
  }
`;

export const ADMIN_EXPORT_LEADS = gql`
  query AdminExportLeads($filters: LeadFiltersInput) {
    adminExportLeads(filters: $filters)
  }
`;

// ─── Mutations ───────────────────────────────────────────────────────────────

export const ADMIN_CREATE_LEAD = gql`
  ${LEAD_LIST_FIELDS}
  mutation AdminCreateLead($input: CreateLeadInput!) {
    adminCreateLead(input: $input) {
      ...LeadListFields
    }
  }
`;

export const ADMIN_UPDATE_LEAD = gql`
  ${LEAD_DETAIL_FIELDS}
  mutation AdminUpdateLead($id: ID!, $input: UpdateLeadInput!) {
    adminUpdateLead(id: $id, input: $input) {
      ...LeadDetailFields
    }
  }
`;

export const ADMIN_UPDATE_LEAD_STATUS = gql`
  ${LEAD_DETAIL_FIELDS}
  mutation AdminUpdateLeadStatus($input: UpdateLeadStatusInput!) {
    adminUpdateLeadStatus(input: $input) {
      ...LeadDetailFields
    }
  }
`;

export const ADMIN_ADD_LEAD_INTERACTION = gql`
  ${LEAD_DETAIL_FIELDS}
  mutation AdminAddLeadInteraction($input: AddLeadInteractionInput!) {
    adminAddLeadInteraction(input: $input) {
      ...LeadDetailFields
    }
  }
`;

export const ADMIN_ASSIGN_LEAD = gql`
  ${LEAD_DETAIL_FIELDS}
  mutation AdminAssignLead($input: AssignLeadInput!) {
    adminAssignLead(input: $input) {
      ...LeadDetailFields
    }
  }
`;

export const ADMIN_DELETE_LEAD = gql`
  mutation AdminDeleteLead($id: ID!) {
    adminDeleteLead(id: $id)
  }
`;

export const ADMIN_CONVERT_LEAD = gql`
  ${LEAD_DETAIL_FIELDS}
  mutation AdminConvertLead($input: ConvertLeadInput!) {
    adminConvertLead(input: $input) {
      lead {
        ...LeadDetailFields
      }
      organizationId
      organizationName
      isNewUser
    }
  }
`;
